import { useEffect, useRef } from "react";
import {
  type SupplierSeoData,
  generateTitle,
  generateDescription,
  generateOgImageUrl,
  generateCanonicalUrl,
  generateJsonLd,
} from "@/lib/seoHelpers";

/**
 * Sets <head> meta tags for a supplier's public page.
 * Handles document.title, meta description, OG, Twitter, canonical, JSON-LD.
 * Cleans up on unmount.
 */
export function useSupplierMeta(
  supplier: SupplierSeoData | null,
  isCustomDomain: boolean,
  hostname?: string
) {
  const originalTitle = useRef(document.title);
  const injectedElements = useRef<HTMLElement[]>([]);

  useEffect(() => {
    if (!supplier) return;

    const title = generateTitle(supplier);
    const description = generateDescription(supplier);
    const image = generateOgImageUrl(supplier);
    const canonical = generateCanonicalUrl(supplier, isCustomDomain, hostname);
    const jsonLd = generateJsonLd(supplier, canonical);

    // Title
    document.title = title;

    // Helper to set or create meta tag
    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (el) {
        el.setAttribute("content", content);
      } else {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        el.setAttribute("content", content);
        document.head.appendChild(el);
        injectedElements.current.push(el);
      }
    };

    // Meta description
    setMeta("name", "description", description);

    // OG tags
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:image", image);
    setMeta("property", "og:site_name", "cenik.ai");

    // Twitter card
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);

    // Canonical
    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonicalEl) {
      canonicalEl.href = canonical;
    } else {
      canonicalEl = document.createElement("link");
      canonicalEl.rel = "canonical";
      canonicalEl.href = canonical;
      document.head.appendChild(canonicalEl);
      injectedElements.current.push(canonicalEl);
    }

    // JSON-LD
    const scriptEl = document.createElement("script");
    scriptEl.type = "application/ld+json";
    scriptEl.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(scriptEl);
    injectedElements.current.push(scriptEl);

    return () => {
      document.title = originalTitle.current;
      for (const el of injectedElements.current) {
        el.remove();
      }
      injectedElements.current = [];
    };
  }, [supplier, isCustomDomain, hostname]);
}
